import { states } from '@/data/@consts/states';
import { CatalogSort } from '@/data/catalog/search';
import type { Plan } from '@/models/bootstrap/plan';
import { Features } from '@/models/bootstrap/plan';
import type { Taxonomy } from '@/models/bootstrap/taxonomy';
import type { Institution } from '@/models/catalog/course';
import type {
  CatalogFilterSchema,
  FilterOption,
} from '@/packages/courses/types/schema';
import { catalogUrlKeys } from '@/packages/courses/types/url-params';

const courseTypesOptions = (taxonomy: Taxonomy): FilterOption[] => {
  if (!taxonomy.course_types?.length) return [];
  return taxonomy.course_types.map((ct) => ({
    label: ct.label,
    value: ct.type,
  }));
};

const modalitiesOptions = (taxonomy: Taxonomy): FilterOption[] => {
  if (!taxonomy.modalities?.length) return [];
  return taxonomy.modalities.map((m) => ({
    label: m.name,
    value: m.value,
  }));
};

const knowledgeAreasOptions = (taxonomy: Taxonomy): FilterOption[] => {
  if (!taxonomy.knowledge_areas?.length) return [];
  return taxonomy.knowledge_areas.map((ka) => ({
    label: ka.description_pt_br,
    value: String(ka.id),
  }));
};

const institutionsOptions = (institutions: Institution[]): FilterOption[] => {
  if (!institutions?.length) return [];
  return institutions.map((i) => ({
    label: i.name,
    value: String(i.id),
  }));
};

const locationOptions = (): {
  statesOptions: FilterOption[];
  citiesOptionsByState: Record<string, FilterOption[]>;
} => {
  const statesOptions: FilterOption[] = states.map((s) => ({
    label: `${s.name} (${s.acronym})`,
    value: s.acronym,
  }));
  const citiesOptionsByState: Record<string, FilterOption[]> = {};
  for (const s of states) {
    citiesOptionsByState[s.acronym] = s.cities.map((c) => ({
      label: c,
      value: c,
    }));
  }
  return { statesOptions, citiesOptionsByState };
};

const sortOptions = (): FilterOption[] => {
  return [
    { label: 'Relevância', value: CatalogSort.Relevance },
    { label: 'Menor preço', value: CatalogSort.PriceAsc },
    { label: 'Maior preço', value: CatalogSort.PriceDesc },
  ];
};

export interface CreateCatalogSchemaParams {
  taxonomy: Taxonomy;
  institutions: Institution[];
  plan?: Plan | null;
}

export const createCatalogSchema = ({
  taxonomy,
  institutions,
  plan,
}: CreateCatalogSchemaParams): CatalogFilterSchema => {
  const courseTypesOpts = courseTypesOptions(taxonomy);
  const modalitiesOpts = modalitiesOptions(taxonomy);
  const knowledgeAreasOpts = knowledgeAreasOptions(taxonomy);
  const institutionsOpts = institutionsOptions(institutions);
  const { statesOptions, citiesOptionsByState } = locationOptions();
  const sortOpts = sortOptions();

  const filterIdsToDisplay: (typeof catalogUrlKeys)[keyof typeof catalogUrlKeys][] =
    [
      catalogUrlKeys.course_types,
      catalogUrlKeys.modalities,
      catalogUrlKeys.knowledge_areas,
      catalogUrlKeys.institutions,
      catalogUrlKeys.location,
      catalogUrlKeys.price_min,
      catalogUrlKeys.price_max,
      catalogUrlKeys.terms,
    ];
  const filterIdsToReset: (typeof catalogUrlKeys)[keyof typeof catalogUrlKeys][] =
    [...filterIdsToDisplay, catalogUrlKeys.page, catalogUrlKeys.sort];

  const companySwitches: CatalogFilterSchema = [];
  if (plan?.features?.some((f) => f.name === Features.FullIncluded)) {
    companySwitches.push({
      id: catalogUrlKeys.free_courses,
      type: 'switch',
      label: 'Apenas cursos gratuitos',
    });
  }
  if (plan?.features?.some((f) => f.name === Features.UnibradPos)) {
    companySwitches.push({
      id: catalogUrlKeys.unibrad_included,
      type: 'switch',
      label: 'Pós-graduação UniBrad',
    });
  }

  const schema = [
    {
      id: 'filter_cloud',
      type: 'tagCloud',
      filterIdsToDisplay: [
        ...filterIdsToDisplay,
        ...companySwitches.map((s) => s.id),
      ],
      filterIdsToReset: [
        ...filterIdsToReset,
        ...companySwitches.map((s) => s.id),
      ],
    },
    {
      id: catalogUrlKeys.sort,
      type: 'inlineSelect',
      title: 'Ordenar por',
      placeholder: 'Ordenação',
      options: sortOpts,
      defaultValue: CatalogSort.Relevance,
    },
    { id: 'divider', type: 'divider' },
    {
      id: catalogUrlKeys.terms,
      type: 'text',
      title: 'Buscar por',
      placeholder: 'Termo buscado',
    },
    ...companySwitches,
    ...(courseTypesOpts.length
      ? [
          {
            id: catalogUrlKeys.course_types,
            type: 'checkbox' as const,
            title: 'Categorias',
            options: courseTypesOpts,
          },
        ]
      : []),
    ...(institutionsOpts.length
      ? [
          {
            id: catalogUrlKeys.institutions,
            type: 'select' as const,
            title: 'Instituição',
            placeholder: 'Selecione',
            multiple: true,
            options: institutionsOpts,
          },
        ]
      : []),
    ...(modalitiesOpts.length
      ? [
          {
            id: catalogUrlKeys.modalities,
            type: 'checkbox' as const,
            title: 'Modalidade',
            options: modalitiesOpts,
          },
        ]
      : []),
    {
      id: catalogUrlKeys.location as 'location',
      type: 'location',
      title: 'Localização',
      statesOptions,
      citiesOptionsByState,
    },
    {
      id: catalogUrlKeys.price_max as 'price_min' | 'price_max',
      type: 'price',
      title: 'Valor máximo por mês',
    },
    ...(knowledgeAreasOpts.length
      ? [
          {
            id: catalogUrlKeys.knowledge_areas,
            type: 'checkbox' as const,
            title: 'Temas',
            options: knowledgeAreasOpts,
          },
        ]
      : []),
  ];

  return schema as CatalogFilterSchema;
};
